import * as React from 'react'

import { Check, ChevronDown } from 'lucide-react'
import { Controller, useFormContext, type RegisterOptions } from 'react-hook-form'

import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useScrollThumb } from '@/shared/hooks/useScrollThumb'
import { ScrollThumb } from '@/shared/ui/ScrollThumb'

import { controlVariants, type ControlSize } from './control-styles'
import { useControlName } from './lib/use-control-name'

/**
 * Seleção sobre `Popover` + `Command` (cmdk), não `<select>` nativo nem o
 * `Select` do Radix.
 *
 * O nativo foi descartado primeiro: em celular abre o seletor do sistema
 * operacional (familiar, zero código), mas não aceita os tokens de controle
 * sem gambiarra de `appearance`, e a lista não pertence à árvore da página —
 * dentro de um modal abre deslocada.
 *
 * O `Select` do Radix foi a segunda tentativa, com busca acrescentada por
 * cima: um `<input>` dentro do `SelectContent` brigando por teclado com o
 * typeahead nativo do primitivo (que intercepta letra a letra para pular
 * item). O `Command` já É lista + busca — sem essa disputa — e é o
 * componente que o próprio catálogo do shadcn recomenda para select
 * pesquisável (padrão "Combobox").
 *
 * Aceita os dois modos dos demais controles de escolha: com `value`/`onChange`
 * opera controlado; sem eles, registra-se pelo `name`.
 */

export type SelectOption = {
  value: string
  label: React.ReactNode
  disabled?: boolean
}

type SelectPresentationProps = {
  value: string
  onValueChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  disabled?: boolean
  size?: ControlSize
  className?: string
  /** Oferece uma entrada para desfazer a escolha, em campo não obrigatório. */
  isClearable?: boolean
  clearLabel?: string
  'aria-label'?: string
}

/** Só identifica o item na lista do `Command` — nunca chega ao formulário. */
const CLEAR_VALUE = '__select-field-clear__'

/** Sem acento e sem diferença de caixa — "unidade" casa com "Unidade". */
function normalizeForSearch(value: string) {
  return value
    .trim()
    .toLocaleLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
}

function SelectPresentation({
  value,
  onValueChange,
  options,
  placeholder = 'Selecione',
  isClearable = false,
  clearLabel = 'Limpar seleção',
  disabled,
  size,
  className,
  ...rest
}: SelectPresentationProps) {
  const [open, setOpen] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState('')
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const { ref: scrollRef, thumbRef, visible, onScroll } = useScrollThumb(options)

  const filteredOptions = searchTerm
    ? options.filter(option =>
        normalizeForSearch(String(option.label)).includes(normalizeForSearch(searchTerm))
      )
    : options

  const selected = options.find(option => option.value === value)

  const select = (next: string) => {
    onValueChange(next)
    setOpen(false)
    // `setOpen(false)` aqui não passa por `onOpenChange` — esse callback só
    // dispara quando o próprio Radix decide fechar (Escape, clique fora).
    setSearchTerm('')
  }

  return (
    <Popover
      open={open}
      onOpenChange={next => {
        setOpen(next)
        // Sem isto, reabrir o select herdaria o filtro da vez anterior.
        if (!next) setSearchTerm('')
      }}
    >
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            controlVariants({ size }),
            'flex items-center justify-between gap-2',
            className
          )}
          {...rest}
        >
          <span
            className={cn('truncate text-left', !selected && 'text-muted-foreground')}
          >
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        // Dentro de um modal, o portal vai para o nó do próprio Dialog: o
        // `react-remove-scroll` dele cancela o `touchmove` de tudo que está
        // fora da sua árvore, e a lista ficava sem rolagem no toque. Fora de
        // modal, `undefined` mantém o portal no `body`, como de praxe.
        container={triggerRef.current?.closest('[role="dialog"]') ?? undefined}
        className="w-[var(--radix-popover-trigger-width)] rounded-[var(--control-r-md)] border-2 border-contour p-0 shadow-lg"
      >
        {/* `label`, não `aria-label` no `CommandInput`: o cmdk dá ao input um
            `aria-labelledby` próprio, apontando para este rótulo — ele vence
            qualquer `aria-label` passado directo no elemento. */}
        <Command shouldFilter={false} label="Buscar opção">
          <CommandInput
            value={searchTerm}
            onValueChange={setSearchTerm}
            placeholder="Buscar"
          />
          <div className="relative overflow-hidden">
            <CommandList ref={scrollRef} onScroll={onScroll}>
              <CommandEmpty>Nenhuma opção encontrada</CommandEmpty>
              {/* Só com algo escolhido: "limpar" num campo vazio é uma linha
                  a mais para ler que não faz nada. A opção de limpar nunca é
                  filtrada pelo texto digitado. */}
              {isClearable && value ? (
                <CommandItem value={CLEAR_VALUE} onSelect={() => select('')}>
                  {clearLabel}
                </CommandItem>
              ) : null}
              {filteredOptions.map(option => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  onSelect={() => select(option.value)}
                >
                  {option.label}
                  {option.value === value ? <Check className="h-4 w-4 shrink-0" /> : null}
                </CommandItem>
              ))}
            </CommandList>
            <ScrollThumb visible={visible} thumbRef={thumbRef} />
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

type SelectFieldProps = Omit<
  SelectPresentationProps,
  'value' | 'onValueChange'
> & {
  /** Só é necessário fora de `<Field>` — ver `useControlName`. */
  name?: string
  value?: string
  onChange?: (value: string) => void
  rules?: RegisterOptions
}

export const SelectField = Object.assign(
  function SelectField({ name, value, onChange, rules, ...rest }: SelectFieldProps) {
    if (onChange) {
      return <SelectPresentation value={value ?? ''} onValueChange={onChange} {...rest} />
    }

    return <RegisteredSelect name={name} rules={rules} {...rest} />
  },
  { displayName: 'SelectField' }
)

function RegisteredSelect({
  name: nameProp,
  rules,
  ...rest
}: Omit<SelectFieldProps, 'value' | 'onChange'>) {
  const name = useControlName(nameProp)
  const { control } = useFormContext()

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => (
        <SelectPresentation
          value={(field.value as string) ?? ''}
          onValueChange={field.onChange}
          {...rest}
        />
      )}
    />
  )
}
